import { lazy, Suspense, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useMe } from "./api/me";
import { useAuth } from "./auth/AuthProvider";
import { HOME } from "./components/nav";
import { Button, ErrorNote, LoadingScreen } from "./components/ui";
import { LandingPage } from "./pages/LandingPage";
import { rememberedRole, rememberRole, type Role } from "./lib/role";

/**
 * Pages load when they're first opened, so the homepage downloads only what it needs (fast on phones).
 * LandingPage stays in the first download: it's what signed-out visitors see first.
 */
const AddHomeworkPage = lazy(async () => ({ default: (await import("./pages/AddHomeworkPage")).AddHomeworkPage }));
const AnnouncementsPage = lazy(async () => ({ default: (await import("./pages/AnnouncementsPage")).AnnouncementsPage }));
const AssignmentsPage = lazy(async () => ({ default: (await import("./pages/AssignmentsPage")).AssignmentsPage }));
const CalendarPage = lazy(async () => ({ default: (await import("./pages/CalendarPage")).CalendarPage }));
const ChecklistPage = lazy(async () => ({ default: (await import("./pages/ChecklistPage")).ChecklistPage }));
const ContactPage = lazy(async () => ({ default: (await import("./pages/ContactPage")).ContactPage }));
const DashboardPage = lazy(async () => ({ default: (await import("./pages/DashboardPage")).DashboardPage }));
const ImportPage = lazy(async () => ({ default: (await import("./pages/ImportPage")).ImportPage }));
const MorePage = lazy(async () => ({ default: (await import("./pages/MorePage")).MorePage }));
const OnboardingPage = lazy(async () => ({ default: (await import("./pages/OnboardingPage")).OnboardingPage }));
const ParentHomePage = lazy(async () => ({ default: (await import("./pages/ParentHomePage")).ParentHomePage }));
const RoleChoicePage = lazy(async () => ({ default: (await import("./pages/RoleChoicePage")).RoleChoicePage }));
const SchoolsPage = lazy(async () => ({ default: (await import("./pages/SchoolsPage")).SchoolsPage }));
const PrivacyPage = lazy(async () => ({ default: (await import("./pages/PrivacyPage")).PrivacyPage }));
const ProfilePage = lazy(async () => ({ default: (await import("./pages/ProfilePage")).ProfilePage }));
const SchedulePage = lazy(async () => ({ default: (await import("./pages/SchedulePage")).SchedulePage }));
const SettingsPage = lazy(async () => ({ default: (await import("./pages/SettingsPage")).SettingsPage }));
const TermsPage = lazy(async () => ({ default: (await import("./pages/TermsPage")).TermsPage }));
const AppShell = lazy(async () => ({ default: (await import("./components/AppShell")).AppShell }));

export default function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/schools" element={<SchoolsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="*" element={<SignedInGate />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Signed out → landing page. Signed in without an account → "Who's signing in?" (student or parent).
 * A parent (or anyone a student listed as a parent) → parent home. A student without a profile → onboarding. Otherwise → the app (the student's own, or a child's for a parent).
 */
function SignedInGate() {
  const { user, loading, signOutNow, actingAs, setActingAs } = useAuth();
  const me = useMe();
  // Signed in without an account: student or parent? Remembered on this device.
  const [chosenRole, setChosenRole] = useState<Role | null>(null);
  const role = chosenRole ?? (user ? rememberedRole(user.uid) : null);
  const choose = (next: Role | null) => {
    if (user) rememberRole(user.uid, next);
    setChosenRole(next);
  };

  if (loading) return <LoadingScreen />;
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  if (me.isPending) return <LoadingScreen />;
  if (me.isError) {
    return (
      <main className="mx-auto max-w-md space-y-4 px-4 py-16">
        <ErrorNote message={me.error.message} />
        <div className="flex gap-3">
          {actingAs ? (
            <Button onClick={() => setActingAs(null)}>Back to my accounts</Button>
          ) : (
            <Button onClick={() => me.refetch()}>Try again</Button>
          )}
          <Button variant="secondary" onClick={signOutNow}>Sign out</Button>
        </div>
      </main>
    );
  }

  const { profile, linkedStudents, viewer } = me.data;
  if (!profile && role !== "student" && (linkedStudents.length > 0 || role === "parent")) {
    return (
      <ParentHomePage
        students={linkedStudents}
        onCheckAgain={() => me.refetch()}
        checking={me.isFetching}
        onSetUpOwnAccount={() => choose("student")}
      />
    );
  }
  if (!profile && role === null) return <RoleChoicePage onChoose={choose} />;
  if (!profile) {
    return (
      <Routes>
        <Route path="/welcome" element={<OnboardingPage />} />
        <Route path="*" element={<Navigate to="/welcome" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell profile={profile} viewer={viewer} />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/checklist" element={<ChecklistPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/assignments" element={<AssignmentsPage />} />
        <Route path="/announcements" element={<AnnouncementsPage />} />
        <Route path="/add" element={<AddHomeworkPage />} />
        <Route path="/import" element={<ImportPage />} />
        <Route path="/schedule" element={<SchedulePage profile={profile} />} />
        <Route path="/profile" element={<ProfilePage profile={profile} />} />
        <Route path="/settings" element={<SettingsPage profile={profile} />} />
        <Route path="/more" element={<MorePage />} />
      </Route>
      <Route path="*" element={<Navigate to={HOME} replace />} />
    </Routes>
  );
}
